"use client";

import type { LoginState, MedplumClient } from "@medplum/core";
import { clearMedplumCookieAction } from "@/actions/auth/clear-medplum-cookie";
import { clearSessionExpiredMarkerAction } from "@/actions/auth/clear-session-expired-marker";
import { setMedplumCookie } from "@/actions/auth/set-medplum-cookie";
import { resetSessionExpiration } from "@/lib/auth/session-expired";
import { createLogger } from "@/lib/logger";

const logger = createLogger("persist-completed-login");

/** Persists an explicitly verified login and clears the previous expiry generation. */
export async function persistCompletedLogin(activeLogin: LoginState): Promise<void> {
  const cookieResult = await setMedplumCookie(activeLogin);
  if (!cookieResult.success) {
    throw new Error(cookieResult.message);
  }

  // Un-latch the recovery banner. Its state is module memory, and every caller finishes with a
  // client-side router.push, so the document — and the latch — outlive the sign-in. Clearing the
  // cookie below does not cover this: nothing reads that cookie back to un-latch. Reset rather
  // than markSessionActive, because a login may be a *different* profile and the retained
  // expiredProfileReference would then be compared against the wrong identity.
  resetSessionExpiration();

  // Cosmetic cleanup of a short-lived marker — the login is already durable, so failing
  // here must not report a completed sign-in as failed.
  const clearMarkerResult = await clearSessionExpiredMarkerAction();
  if (!clearMarkerResult.success) {
    logger.warn("Failed to clear the expiry marker after a completed login", {
      error: clearMarkerResult.message,
    });
  }
}

/**
 * Persists a login found in browser storage so the app can resume it. Returns false, after
 * clearing the login and the cookie, when the login has no refresh token: that session dies at
 * the next access-token expiry, mid-visit if a recording is running, and cannot be refreshed
 * because the SDK has nothing to refresh with. One fresh sign-in migrates it. Logins issued
 * before `offline_access` was requested at sign-in are the only ones without a refresh token.
 */
export async function resumeStoredLogin({
  medplum,
  activeLogin,
}: {
  medplum: MedplumClient;
  activeLogin: LoginState;
}): Promise<boolean> {
  if (!activeLogin.refreshToken) {
    logger.info("Stored login has no refresh token; requiring a fresh sign-in", {
      profileReference: activeLogin.profile?.reference,
    });
    await medplum.clear();
    await clearMedplumCookieAction();
    return false;
  }
  await persistCompletedLogin(activeLogin);
  return true;
}
